<script setup lang="ts">
import type {
  MaskObject,
  PolylineObject,
  SegmentationData,
} from '@src/store/analysis/data-typing';

import type { SliceEsEdPrecheckResult } from './analysisPrecheck';

import type { ChartSeries } from '@/src/components/analysis/ocgChartRenderer';

import type { DicomPullRequest } from '@/src/components/backend/payload-typing';

import { useUpdatedReportStore } from '@src/store/report-store';

import { computed, ref, useTemplateRef } from 'vue';

import OcgCurveChart from '@/src/components/analysis/OcgCurveChart.vue';
import {
  getAllImageUIDs,
} from '@/src/components/backend/payload-tools';
import LoadSaveAnalysisStateDialog from '@/src/components/LoadSaveAnalysisStateDialog.vue';
import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useAlert } from '@/src/composables/useAlert';
import { useMaskExtractor } from '@/src/composables/useMaskExtractor';
import { useSegmentationGateway } from '@/src/composables/useSegmentationGateway';
import { useAnalysisStore } from '@/src/store/analysis';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '@/src/store/contours/contour-definitions';
import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';

// import { useSegmentationSettingsStore } from '@/src/store/contours/contour-segmentation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { Orientation } from '@/src/store/dicom-web/classification-typing';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';
import { useAppState } from '@/src/store/ocg-app-state';
import { session } from '@/src/supabase';

import { checkSliceEsEd } from './analysisPrecheck';

defineProps({
  showDialog: {
    type: Boolean,
    default: false,
  },
});

let GATEWAY_URL = import.meta.env.VITE_OCG_BACKEND_GATEWAY_API;
GATEWAY_URL = GATEWAY_URL.replace(/\/+$/, '');

const analysisStore = useAnalysisStore();
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();
// const segmentationSettingsStore = useSegmentationSettingsStore();
const currentStudyStore = useCurrentStudyStore();
const appState = useAppState();
const { getImageSegmentationGeometry } = useMaskExtractor();

const viewerIDs = ['ContoursLeft', 'ContoursRight'];

const alert = useAlert();
const isAnalysisActive = ref(false);
const showPreflightDialog = ref(false);
const calculateBMORE = ref(false);
const calculateBMOREMax = ref(false);
const calculateV5Markers = ref(false);
const calculateVasoconstrictionMap = ref(false);

const loadSaveAnalysisStateDialog = useTemplateRef<
  InstanceType<typeof LoadSaveAnalysisStateDialog>
>('loadSaveAnalysisStateDialog');

// ─── Confirmed Viewport Selections ─────────────────────────────────
interface ConfirmedViewportSelection {
  viewerID: string;
  seriesIndex: number;
  sliceIndex: number;
  /** Index into the sliceAnalysisInfo / dialog card (0 = left, 1 = right) */
  // Can expand in future if we need to support more than 2 viewers
  dialogSlotIndex: number;
}

const confirmedSelections = ref<ConfirmedViewportSelection[]>([]);

const hasDuplicateSelections = computed(() => {
  if (confirmedSelections.value.length < 2) return false;
  const [a, b] = confirmedSelections.value;
  return a.seriesIndex === b.seriesIndex && a.sliceIndex === b.sliceIndex;
});

/**
 * Deduplicated list — if both viewers point to the same series+slice
 * we only need to process once.
 */
const uniqueConfirmedSelections = computed(() => {
  if (!hasDuplicateSelections.value) return confirmedSelections.value;
  return [confirmedSelections.value[0]];
});

// ─── Slice Analysis Info ────────────────────────────────────────────
interface SliceAnalysisInfo {
  sliceIndex: number;
  viewerID: string;
  seriesIndex: number;
  imageCount: number;
  processedImageCount: number;
  breathHoldStart: number;
  breathHoldEnd: number;
  bmoreStart?: number;
  bmoreEnd?: number;
  segmentedPhaseCount: number;
  missingPhaseIndices: number[];
  excludedImageIndices: number[];
  loadingStatus: 'Pending' | 'Loading' | 'Complete';
  esEdPrecheck?: SliceEsEdPrecheckResult;
}

const sliceAnalysisInfo = ref<SliceAnalysisInfo[]>([]);
const selectedSliceOrientation = ref<Record<string, Orientation>>({});
const isPreflightLoading = ref(false);
const preflightProcessedImages = ref(0);
const preflightTotalImages = ref(0);
const preflightCurrentSlice = ref<number | null>(null);
const preflightLoadError = ref<string | null>(null);
const orientationOptions = [
  Orientation.Undefined,
  Orientation.Sax,
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
].map(value => ({
  label: value,
  value,
}));

const hasSliceSummaries = computed(() => sliceAnalysisInfo.value.length > 0);
const hasMissingSegmentationPhases = computed(() =>
  sliceAnalysisInfo.value.some(
    sliceInfo => sliceInfo.missingPhaseIndices.length > 0,
  ),
);
const hasUnclassifiedOrientations = computed(() =>
  sliceAnalysisInfo.value.some((sliceInfo) => {
    const orientation
      = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
        ?? seriesClassification.getClassificationByViewport(sliceInfo.viewerID)
          ?.orientation
          ?? Orientation.Undefined;

    return orientation === Orientation.Undefined;
  }),
);
const preflightProgress = computed(() => {
  if (preflightTotalImages.value <= 0) return 0;
  return (preflightProcessedImages.value / preflightTotalImages.value) * 100;
});

function toOrientation(value: Orientation | string) {
  return Object.values(Orientation).includes(value as Orientation)
    ? (value as Orientation)
    : Orientation.Undefined;
}

function recalculatePreflightProcessedImages() {
  preflightProcessedImages.value = sliceAnalysisInfo.value.reduce(
    (sum, item) => sum + item.processedImageCount,
    0,
  );
}

async function processSliceAnalysis(
  sliceInfo: SliceAnalysisInfo,
  orientation: Orientation,
) {
  const targetSlice = sliceInfo;

  const studyObj = useViewportActions().getStudyObjectByViewerId(
    targetSlice.viewerID,
  );
  if (!studyObj) {
    throw new Error(
      `Failed to retrieve study object for viewer ${targetSlice.viewerID}`,
    );
  }
  const breathholdState = studyObj.getBreathhold();
  const excludedImages = studyObj.getExcludedImages();
  if (!breathholdState || !excludedImages) {
    throw new Error(
      `Failed to retrieve breathhold state or excluded images for viewer ${targetSlice.viewerID}`,
    );
  }

  const imageUIDsfromStore
    = currentStudyStore.getSliceImageUIDsbyView(targetSlice.viewerID) ?? [];

  const missingPhaseIndices: number[] = [];
  let segmentedPhaseCount = 0;
  let processedImageCount = 0;

  for (let i = breathholdState.getStart(); i <= breathholdState.getEnd(); i++) {
    const imageUID = imageUIDsfromStore[i];

    if (imageUID && !excludedImages.has(imageUID)) {
      let hasSegmentation = false;

      try {
        await currentStudyStore.loadVtkImageAndMetaData(imageUID);
      }
      catch (error) {
        console.warn('Failed to load preflight image:', imageUID, error);
      }

      const { myoMask, endoContour, epiContour } = getImageSegmentationGeometry(
        imageUID,
        orientation,
        targetSlice.viewerID,
      );

      if (myoMask.length > 0) {
        hasSegmentation = true;
      }
      else {
        hasSegmentation = endoContour.length > 0 && epiContour.length > 0;
      }

      if (hasSegmentation) {
        segmentedPhaseCount += 1;
      }
      else {
        missingPhaseIndices.push(i + 1);
      }

      processedImageCount += 1;
      targetSlice.processedImageCount = processedImageCount;
      recalculatePreflightProcessedImages();

      if (processedImageCount % 2 === 0) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 0);
        });
      }
    }
  }

  targetSlice.segmentedPhaseCount = segmentedPhaseCount;
  targetSlice.missingPhaseIndices = missingPhaseIndices;

  // Run cardiac cycle ES/ED precheck
  try {
    targetSlice.esEdPrecheck = checkSliceEsEd({
      viewerID: targetSlice.viewerID,
      seriesIndex: targetSlice.seriesIndex,
      sliceIndex: targetSlice.sliceIndex,
      orientation,
    });
  }
  catch (err) {
    console.warn('Failed to perform ES/ED precheck for slice:', err);
  }
}

async function rerunSliceAnalysis(
  sliceInfo: SliceAnalysisInfo,
  orientation: Orientation,
) {
  const targetSlice = sliceInfo;

  preflightLoadError.value = null;
  isPreflightLoading.value = true;
  preflightCurrentSlice.value = targetSlice.sliceIndex;
  targetSlice.loadingStatus = 'Loading';
  targetSlice.processedImageCount = 0;
  targetSlice.segmentedPhaseCount = 0;
  targetSlice.missingPhaseIndices = [];
  recalculatePreflightProcessedImages();
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });

  try {
    await processSliceAnalysis(sliceInfo, orientation);
    targetSlice.loadingStatus = 'Complete';
  }
  catch (error) {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    console.error('Preflight slice refresh failed:', error);
  }
  finally {
    preflightCurrentSlice.value = null;
    isPreflightLoading.value = false;
  }
}

async function handleSliceOrientationChange(
  sliceInfo: SliceAnalysisInfo,
  orientationValue: Orientation | string,
) {
  const orientation = toOrientation(orientationValue);
  const sliceKey = String(sliceInfo.sliceIndex);

  selectedSliceOrientation.value[sliceKey] = orientation;
  seriesClassification.adjustClassificationByViewport(sliceInfo.viewerID, {
    orientation,
    mode: 'Manual',
  });

  await rerunSliceAnalysis(sliceInfo, orientation);
}

function yieldToUI() {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

// AbortController for cancelling in-flight analysis requests
let analysisAbortController: AbortController | null = null;

analysisStore.resetStudy();

async function pollForAnalysisResults(jobId: string) {
  const pollInterval = 3000; // 3 seconds
  const maxAttempts = 200; // ~10 minutes

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise(resolve => setTimeout(resolve, pollInterval));

    const response = await fetch(`${GATEWAY_URL}/jobs/${jobId}`);
    if (!response.ok) continue;

    const job = await response.json();
    const status = job.status?.status;

    if (status === 'processing') {
      alert.info(`Analysis: ${job.status.message || 'Processing'} (${job.status.progress || 0}%)`);
    }
    else if (status === 'complete') {
      alert.success('Analysis complete! Downloading results...');

      if (job.zip_url) {
        try {
          const gateway = useSegmentationGateway();
          const zipRes = await gateway.fetchJobZipResults(job.zip_url);
          if (zipRes.analysisResponseData) {
            handleResponse(zipRes.analysisResponseData);
            loadSaveAnalysisStateDialog.value?.backgroundAutosave(
              currentStudyStore.getStudyID,
            );
            alert.hideAll();
            isAnalysisActive.value = false;
            appState.currentView = 'GraphicalAnalysis';
          }
        }
        catch (err: any) {
          alert.error(`Failed to process analysis results ZIP: ${err.message}`);
        }
      }
      return;
    }
    else if (status === 'failed') {
      alert.error(`Analysis failed: ${job.error}`);
      return;
    }
    else if (status === 'aborted') {
      alert.warning('Analysis was aborted.');
      return;
    }
  }

  alert.warning('Analysis polling timed out.');
}

async function submitAnalysisToGateway() {
  if (isAnalysisActive.value) {
    abortAnalysis();
    return;
  }

  isAnalysisActive.value = true;
  alert.info('Preparing data for analysis...');

  try {
    // 1. Collect image UIDs and metadata (same as before)
    const {
      include,
      bmoreTarget,
      breathHoldCrops,
      allImageUIDs,
      excludedImageUIDs,
    } = getImageUIDsBmoreUIDs(calculateBMORE.value);

    const viewerIDsForRequest = uniqueConfirmedSelections.value.map(
      sel => sel.viewerID,
    );

    const { dicomPullRequestObject, imageUIDLookupTable, metaData }
      = await getAllImageUIDs({
        viewerIDs: viewerIDsForRequest,
        include: allImageUIDs,
        exclude: excludedImageUIDs,
        requiresImageLookup: true,
      });

    if (!dicomPullRequestObject) {
      alert.error('No images found for analysis.');
      isAnalysisActive.value = false;
      return;
    }

    // 2. Build segmentation data (same as before)
    const segmentationData = await getSegmentationData(dicomPullRequestObject);
    if (!segmentationData) {
      isAnalysisActive.value = false;
      return;
    }

    // 3. Check coverage (same as before)
    const allUIDs = include;
    const allUIDsWithContours = Object.keys(segmentationData);
    const missingContours = allUIDs.filter(
      uid => !allUIDsWithContours.includes(uid),
    );
    if (missingContours.length > 0) {
      alert.error(
        `Please ensure all images are segmented. Missing contours for ${missingContours.length} images.`,
      );
      isAnalysisActive.value = false;
      return;
    }

    // 4. Enrich metadata (same as before)
    metaData.bmoreTarget = calculateBMORE.value ? bmoreTarget : {};
    metaData.breathHoldCrops = breathHoldCrops;
    metaData.excludedImageUIDs = excludedImageUIDs;
    metaData.sliceClassification = getSliceClassification();
    metaData.calculateBMORE = calculateBMORE.value;
    metaData.calculateBMOREMax = calculateBMOREMax.value;
    metaData.calculateVasoconstrictionMap = calculateVasoconstrictionMap.value;
    metaData.calculateV5Biomarkers = calculateV5Markers.value;

    // 5. Strip imageData from DICOM requests — worker will fetch images itself
    const lightweightDicomRequests = dicomPullRequestObject.map(item => ({
      StudyInstanceUID: item.StudyInstanceUID,
      SeriesInstanceUID: item.SeriesInstanceUID,
      SOPInstanceUID: item.SOPInstanceUID,
    }));

    // 6. Submit to gateway — NO image upload, NO streaming
    alert.info('Submitting analysis job...');
    const response = await fetch(`${GATEWAY_URL}/jobs/analysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: session.value?.user?.id,
        study_instance_uid: currentStudyStore.getStudyID,
        dicom_pull_request_array: lightweightDicomRequests,
        image_uid_lookup_table: imageUIDLookupTable,
        segmentation_data: segmentationData,
        meta_data: metaData,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(`HTTP ${response.status}: ${err.detail}`);
    }

    const { job_id } = await response.json();
    alert.success(`Analysis job created: ${job_id}`);

    // 7. Poll for results
    await pollForAnalysisResults(job_id);
  }
  catch (error: any) {
    if (error.name === 'AbortError') return;
    alert.error(`Failed to submit analysis: ${error.message}`);
  }
  finally {
    isAnalysisActive.value = false;
  }
}

// ─── getSegmentationData ────────────────────────────────────────────
async function getSegmentationData(dicomPullRequestObject: DicomPullRequest[]) {
  const segmentationData: SegmentationData = {};

  // Fast lookup: only process UIDs that are in the pull request
  const requestedUIDs = new Set(
    dicomPullRequestObject.map(r => r.SOPInstanceUID),
  );

  // Process each unique confirmed selection — no reverse-mapping needed

  for (const sel of uniqueConfirmedSelections.value) {
    const orientation
      = selectedSliceOrientation.value[String(sel.dialogSlotIndex)]
        ?? seriesClassification.getClassificationByViewport(sel.viewerID);

    const imageUIDsForSlice
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];

    for (const imageUID of imageUIDsForSlice) {
      // Only process UIDs that are part of the pull request

      if (!requestedUIDs.has(imageUID)) continue;

      if (!contourContainerStore.contourContainers[imageUID]) continue;

      // Ensure the image is loaded

      await currentStudyStore.loadVtkImageAndMetaData(imageUID);

      const { myoMask, endoContour, epiContour } = getImageSegmentationGeometry(
        imageUID,
        orientation,
        sel.viewerID,
      );

      // Insertion point label depends on orientation. For SAX use RvInsertion, for LAX use LaxLvEndoBasalPoint
      const laxOrientations = new Set<Orientation>([
        Orientation.Lax2CV,
        Orientation.Lax3CV,
        Orientation.Lax4CV,
        Orientation.Lax5CV,
      ]);
      const insertionPointLabel = laxOrientations.has(orientation)
        ? ContourLabel.LaxLvEndoBasalPoint
        : ContourLabel.RvInsertion;

      // TODO: insertion point no longer an accurate name, use "reference point" or similar in future
      const [insertionPoint] = convertPointArrayToArray(
        contourContainerStore.contourContainers[imageUID].getContourPointArray({
          label: insertionPointLabel,
          id: encodeContourLabelItemId([0]),
        }),
      );

      if (!segmentationData[imageUID]) {
        segmentationData[imageUID] = [];
      }

      if (myoMask.length === 0) {
        if (endoContour.length === 0 || epiContour.length === 0) {
          alert.error(
            `No segmentation found for imageUID ${imageUID}. `
            + 'Please ensure all images are properly segmented and loaded.',
          );
          return null;
        }

        segmentationData[imageUID].push({
          type: 'PolylineObject',
          contourType: 'ENDO',
          data: endoContour,
        } as PolylineObject);

        segmentationData[imageUID].push({
          type: 'PolylineObject',
          contourType: 'EPI',
          data: epiContour,
        } as PolylineObject);
      }
      else {
        segmentationData[imageUID].push({
          type: 'MaskObject',
          maskType: 'MyoMask',
          data: myoMask as [number, number][],
        } as MaskObject);
      }

      segmentationData[imageUID].push({
        type: 'PolylineObject',
        contourType: 'insertionPoint',
        data: insertionPoint,
      } as PolylineObject);
    }
  }

  return segmentationData;
}

// ─── handleResponse ─────────────────────────────────────────────────
function handleResponse(responseData: any) {
  const biomarkerContainer = analysisStore.resetStudy();
  const analysisViewers = ['AnalysisLeft', 'AnalysisRight'];

  biomarkerContainer.populateFromResponse(responseData);

  const dynamicMaskTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Dynamic');
  const staticMaskTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Static');
  const polarMapTypes
    = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('PolarMap');

  // Map each analysis viewer back to the confirmed selection.
  // When duplicated, both analysis viewers get the same indices
  // (from the single processed selection).
  for (let i = 0; i < analysisViewers.length; i++) {
    const viewID = analysisViewers[i];
    const sel = confirmedSelections.value[i];

    const sourceViewerID
      = sel?.viewerID ?? confirmedSelections.value[0].viewerID;
    const sourceIndices
      = viewportStateStore.getSelectedImageIndices(sourceViewerID);

    viewportStateStore.setSelectedImageIndices(viewID, sourceIndices, true);

    if (staticMaskTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'Static',
        staticMaskTypes[0],
      );
    }
    if (dynamicMaskTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'Dynamic',
        dynamicMaskTypes[0],
      );
    }

    if (polarMapTypes[0]) {
      viewportStateStore.setSelectedMarker(
        viewID,
        'PolarMap',
        polarMapTypes[0],
      );
    }
    viewportStateStore.setShowContours(viewID, false);
  }

  const reportStore = useUpdatedReportStore();
  reportStore.resetStore();
}

// ─── getImageUIDsBmoreUIDs ──────────────────────────────────────────
function getImageUIDsBmoreUIDs(includeBMORETarget = true) {
  const prefetchObject = {
    viewerIDs: [] as string[],
    include: [] as string[],
    allImageUIDs: [] as string[],
    excludedImageUIDs: [] as string[],
    bmoreTarget: {} as Record<string, [number, number]>,
    breathHoldCrops: {} as Record<string, { start: number; end: number }>,
  };

  // Use uniqueConfirmedSelections so we don't double-fetch for duplicates
  uniqueConfirmedSelections.value.forEach((sel) => {
    prefetchObject.viewerIDs.push(sel.viewerID);

    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];
    const studyObj = useViewportActions().getStudyObjectByViewerId(
      sel.viewerID,
    );
    if (!studyObj) {
      throw new Error(
        `Failed to retrieve study object for viewer ${sel.viewerID}`,
      );
    }
    const breathholdState = studyObj.getBreathhold();
    const excludedImages = studyObj.getExcludedImages();
    const start = breathholdState.getStart();
    const end = breathholdState.getEnd();

    const seriesUID = currentStudyStore.getSeriesUID(sel.seriesIndex);
    const selectionKey = `${seriesUID}_${sel.sliceIndex}`;

    if (includeBMORETarget) {
      const bmoreStart = breathholdState.getSalientPhaseLocation('BMORE0');
      const bmoreEnd = breathholdState.getSalientPhaseLocation('BMORE30');
      if (bmoreStart === undefined || bmoreEnd === undefined) {
        alert.error(
          'BMORE start or end location is not defined.',
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE start or end location is not defined',
        );
      }
      const bmoreStartUID = imageUIDsfromStore[bmoreStart];
      const bmoreEndUID = imageUIDsfromStore[bmoreEnd];

      if (
        excludedImages.has(bmoreStartUID)
        || excludedImages.has(bmoreEndUID)
      ) {
        alert.error(
          'BMORE start or end image is in the excluded images for the selected breathhold. Please adjust the breathhold selection.',
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE start or end image is in the excluded images for the selected breathhold',
        );
      }

      if (bmoreStart < start || bmoreEnd > end) {
        alert.error(
          `BMORE range (${bmoreStart}-${bmoreEnd}) is out of bounds for the selected breathhold (${start}-${end}). Please adjust the breathhold selection.`,
        );
        isAnalysisActive.value = false;
        throw new Error(
          'BMORE range is out of bounds for the selected breathhold',
        );
      }

      prefetchObject.bmoreTarget[selectionKey] = [bmoreStart, bmoreEnd];
    }

    prefetchObject.breathHoldCrops[selectionKey] = { start, end };

    const croppedImageUIDs = imageUIDsfromStore.slice(start, end + 1);
    const cleansedImageUIDs = croppedImageUIDs.filter(
      uid => !excludedImages.has(uid),
    );

    prefetchObject.include.push(...cleansedImageUIDs);
    prefetchObject.allImageUIDs.push(...croppedImageUIDs);
    prefetchObject.excludedImageUIDs.push(...excludedImages);
  });

  return prefetchObject;
}

// ─── getSliceClassification ─────────────────────────────────────────
function getSliceClassification() {
  const sliceClassification: Record<string, Orientation> = {};

  confirmedSelections.value.forEach((sel) => {
    sliceClassification[String(sel.dialogSlotIndex)]
      = selectedSliceOrientation.value[String(sel.dialogSlotIndex)]
        ?? seriesClassification.getClassificationByViewport(sel.viewerID);
  });

  return sliceClassification;
}

// ─── buildSliceAnalysisInfo ─────────────────────────────────────────
async function buildSliceAnalysisInfo() {
  // 1. Snapshot confirmed viewport state at dialog-open time
  confirmedSelections.value = viewerIDs.map((viewerID, idx) => {
    const { seriesIdx, sliceIdx }
      = viewportStateStore.getSelectedImageIndices(viewerID);
    return {
      viewerID,
      seriesIndex: seriesIdx,
      sliceIndex: sliceIdx,
      dialogSlotIndex: idx,
    };
  });

  // 2. Build per-slice analysis info driven from confirmedSelections
  const allSliceInfo: SliceAnalysisInfo[] = [];
  isPreflightLoading.value = true;
  preflightLoadError.value = null;
  preflightCurrentSlice.value = null;
  preflightProcessedImages.value = 0;
  preflightTotalImages.value = 0;

  confirmedSelections.value.forEach((sel) => {
    const studyObj = useViewportActions().getStudyObjectByViewerId(
      sel.viewerID,
    );
    if (!studyObj) {
      throw new Error(
        `Failed to retrieve study object for viewer ${sel.viewerID}`,
      );
    }
    const breathholdState = studyObj.getBreathhold();
    const excludedImages = studyObj.getExcludedImages();
    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(sel.viewerID) ?? [];

    const breathHoldStart = breathholdState.getStart();
    const breathHoldEnd = breathholdState.getEnd();
    const bmoreStart = breathholdState.getSalientPhaseLocation('BMORE0');
    const bmoreEnd = breathholdState.getSalientPhaseLocation('BMORE30');

    const excludedImageIndices: number[] = [];
    let imageCount = 0;

    for (let i = breathHoldStart; i <= breathHoldEnd; i++) {
      const imageUID = imageUIDsfromStore[i];
      if (imageUID) {
        if (excludedImages.has(imageUID)) {
          excludedImageIndices.push(i + 1);
        }
        else {
          imageCount += 1;
        }
      }
    }

    const orientation
      = seriesClassification.getClassificationByViewport(sel.viewerID)
        ?.orientation ?? Orientation.Undefined;
    selectedSliceOrientation.value[String(sel.dialogSlotIndex)] = orientation;

    allSliceInfo.push({
      sliceIndex: sel.dialogSlotIndex,
      viewerID: sel.viewerID,
      seriesIndex: sel.seriesIndex,
      imageCount,
      processedImageCount: 0,
      breathHoldStart,
      breathHoldEnd,
      bmoreStart,
      bmoreEnd,
      segmentedPhaseCount: 0,
      missingPhaseIndices: [],
      excludedImageIndices,
      loadingStatus: 'Pending',
    });
  });

  sliceAnalysisInfo.value = allSliceInfo;
  preflightTotalImages.value = allSliceInfo.reduce(
    (sum, item) => sum + item.imageCount,
    0,
  );

  try {
    for (let idx = 0; idx < sliceAnalysisInfo.value.length; idx++) {
      const sliceInfo = sliceAnalysisInfo.value[idx];
      preflightCurrentSlice.value = sliceInfo.sliceIndex;
      sliceInfo.loadingStatus = 'Loading';

      await yieldToUI();

      const orientation
        = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
          ?? seriesClassification.getClassificationByViewport(sliceInfo.viewerID)
            ?.orientation
            ?? Orientation.Undefined;

      await processSliceAnalysis(sliceInfo, orientation);
      sliceInfo.loadingStatus = 'Complete';
    }
  }
  catch (error: any) {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    console.error('Preflight build failed:', error);
  }
  finally {
    preflightCurrentSlice.value = null;
    isPreflightLoading.value = false;
  }
}

function applySelectedOrientations() {
  sliceAnalysisInfo.value.forEach((sliceInfo) => {
    const orientation
      = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
        ?? Orientation.Undefined;

    seriesClassification.adjustClassificationByViewport(sliceInfo.viewerID, {
      orientation,
    });
  });
}

function cancelPreflightDialog() {
  showPreflightDialog.value = false;
}

function abortAnalysis() {
  if (!isAnalysisActive.value) return;

  if (analysisAbortController) {
    analysisAbortController.abort();
    analysisAbortController = null;
  }

  isAnalysisActive.value = false;
  alert.warning('Analysis aborted.');
}

async function sendImageData() {
  await submitAnalysisToGateway();
  isAnalysisActive.value = false;
}

async function applyRecommendedCrop(sliceInfo: SliceAnalysisInfo) {
  const crop = sliceInfo.esEdPrecheck?.suggestedCrop;
  if (!crop) return;

  const studyObj = useViewportActions().getStudyObjectByViewerId(
    sliceInfo.viewerID,
  );
  if (!studyObj) {
    alert.error('Failed to retrieve study object for slice crop.');
    return;
  }
  const breathholdState = studyObj.getBreathhold();
  if (!breathholdState) {
    console.error('Breathhold state not found');
    return;
  }

  breathholdState.updateStartEnd(
    crop.startImageIndex,
    crop.endImageIndex,
  );
  alert.success(
    `Cropped slice ${sliceInfo.sliceIndex + 1} to (${crop.startImageIndex + 1}–${crop.endImageIndex + 1}).`,
  );

  const orientation
    = selectedSliceOrientation.value[String(sliceInfo.sliceIndex)]
      ?? seriesClassification.getClassificationByViewport(sliceInfo.viewerID)
        ?.orientation
        ?? Orientation.Undefined;

  await rerunSliceAnalysis(sliceInfo, orientation);
}

async function startAnalysisFromDialog() {
  if (isPreflightLoading.value) {
    alert.info(
      'Still preparing slice data. Please wait for pre-check to finish.',
    );
    return;
  }

  if (preflightLoadError.value) {
    alert.error(preflightLoadError.value);
    return;
  }

  if (hasUnclassifiedOrientations.value) {
    alert.error(
      'Please select an orientation classification for every slice before starting analysis.',
    );
    return;
  }

  const slicesWithCropSuggestion = sliceAnalysisInfo.value.filter(
    s => s.esEdPrecheck?.hasCropSuggestion,
  );
  if (slicesWithCropSuggestion.length > 0) {
    const userConfirm = await alert.confirm(
      'One or more of the selected slices start with End-Systole (ES) instead of End-Diastole (ED). We recommend auto-cropping before analyzing. Would you like to automatically crop now?',
    );
    if (userConfirm) {
      for (const s of slicesWithCropSuggestion) {
        await applyRecommendedCrop(s);
      }
      return;
    }
  }

  applySelectedOrientations(); // For now won't really work since we need per slice classification
  showPreflightDialog.value = false;
  await sendImageData();
}

async function beginAnalysis() {
  if (isAnalysisActive.value) {
    abortAnalysis();
    return;
  }

  showPreflightDialog.value = true;
  buildSliceAnalysisInfo().catch((error) => {
    preflightLoadError.value
      = 'Failed while preparing slice data. Please close and reopen this dialog.';
    isPreflightLoading.value = false;
    console.error('Failed to start preflight build:', error);
  });
}

// ─── Preflight Curve Visualization Helpers ──────────────────────────
function getPreflightSliceSeries(sliceInfo: SliceAnalysisInfo): ChartSeries[] {
  const precheck = sliceInfo.esEdPrecheck;
  if (!precheck || !precheck.lvBpAreaRaw || precheck.lvBpAreaRaw.length === 0) {
    return [];
  }

  const seriesList: ChartSeries[] = [];

  if (precheck.lvBpAreaSmoothed && precheck.lvBpAreaSmoothed.length > 0) {
    seriesList.push({
      id: 'lvBpAreaSmoothed',
      name: 'Cavity Area',
      color: 'var(--md-primary)',
      data: precheck.lvBpAreaSmoothed,
      strokeWidth: 1.8,
    });
  }

  if (precheck.lvBpAreaRaw && precheck.lvBpAreaRaw.length > 0) {
    seriesList.push({
      id: 'lvBpAreaRaw',
      name: 'Raw Area',
      color: 'var(--md-outline)',
      data: precheck.lvBpAreaRaw,
      strokeWidth: 1,
      strokeDasharray: '2,2',
      showDots: false,
    });
  }

  return seriesList;
}

defineExpose({
  beginAnalysis,
  abortAnalysis,
  isAnalysisActive,
});
</script>

<template>
  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />

  <v-dialog v-model="showPreflightDialog" max-width="980">
    <v-card class="preflight-dialog">
      <!-- Header -->
      <div class="preflight-header px-5 pt-3 pb-2 d-flex align-center justify-space-between">
        <div>
          <div class="text-body-large font-weight-bold">
            OCG Analysis Pre-Check
          </div>
          <div class="text-body-small text-medium-emphasis">
            Review slice inputs, breathhold ranges, and detected cardiac cycles before running analysis.
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" size="x-small" density="comfortable" @click="cancelPreflightDialog" />
      </div>
      <v-divider />

      <v-card-text class="preflight-body px-5 py-3">
        <!-- Warning alerts -->
        <v-alert
          v-if="hasDuplicateSelections"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          Both viewers are showing the same series and slice. Analysis will process these images once.
        </v-alert>

        <v-alert
          v-if="hasUnclassifiedOrientations"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          Select an orientation classification for every slice before starting analysis.
        </v-alert>

        <v-alert
          v-if="preflightLoadError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          {{ preflightLoadError }}
        </v-alert>

        <!-- Compact Loading strip -->
        <div v-if="isPreflightLoading" class="preflight-loading-banner mb-3">
          <div class="d-flex align-center justify-space-between text-body-small mb-1">
            <span>
              Preparing slice data...
              <span v-if="preflightCurrentSlice !== null" class="font-weight-medium">
                Slice {{ preflightCurrentSlice + 1 }}
              </span>
            </span>
            <span class="text-medium-emphasis font-mono">{{ preflightProcessedImages }} / {{ preflightTotalImages }}</span>
          </div>
          <v-progress-linear :model-value="preflightProgress" color="primary" height="3" rounded />
        </div>

        <!-- Slice Cards Grid (Two columns, compact) -->
        <div v-if="hasSliceSummaries" class="slice-grid">
          <div
            v-for="sliceInfo in sliceAnalysisInfo"
            :key="sliceInfo.viewerID"
            class="slice-panel"
          >
            <!-- Card Header -->
            <div class="slice-panel-header mb-2">
              <div class="d-flex align-center ga-2">
                <span class="slice-title">Slice {{ sliceInfo.sliceIndex + 1 }}</span>
                <span class="slice-viewer-sub">{{ sliceInfo.viewerID }}</span>
              </div>
              <span
                v-if="sliceInfo.loadingStatus === 'Loading'"
                class="status-chip chip-loading"
              >
                <v-progress-circular indeterminate size="10" width="1.5" class="mr-1" />
                Loading
              </span>
              <span
                v-else-if="sliceInfo.esEdPrecheck?.hasCropSuggestion"
                class="status-chip chip-warning"
              >
                Crop Needed
              </span>
              <span
                v-else-if="sliceInfo.esEdPrecheck?.isValid"
                class="status-chip chip-success"
              >
                {{ sliceInfo.esEdPrecheck.nRR }} Cycle{{ sliceInfo.esEdPrecheck.nRR === 1 ? '' : 's' }} Valid
              </span>
              <span
                v-else-if="sliceInfo.esEdPrecheck?.errorMessage"
                class="status-chip chip-error"
              >
                Validation Error
              </span>
            </div>

            <!-- Orientation & Stats Strip in single cohesive row -->
            <div class="slice-classification-wrap">
              <v-select
                v-model="selectedSliceOrientation[String(sliceInfo.sliceIndex)]"
                :items="orientationOptions"
                item-title="label"
                item-value="value"
                label="Classification"
                density="compact"
                variant="outlined"
                :disabled="isPreflightLoading"
                hide-details
                class="orientation-select"
                @update:model-value="handleSliceOrientationChange(sliceInfo, $event)"
              />
            </div>
            <div class="slice-controls-row mb-2">
              <div class="slice-meta-strip">
                <div class="meta-item">
                  <span class="meta-label">Images:</span>
                  <span class="meta-val">{{ sliceInfo.processedImageCount }}/{{ sliceInfo.imageCount }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Breathhold:</span>
                  <span class="meta-val">{{ sliceInfo.breathHoldStart + 1 }}–{{ sliceInfo.breathHoldEnd + 1 }}</span>
                </div>
                <div v-if="calculateBMORE" class="meta-item">
                  <span class="meta-label">BMORE:</span>
                  <span class="meta-val">{{ sliceInfo.bmoreStart !== undefined ? `${sliceInfo.bmoreStart + 1}–${sliceInfo.bmoreEnd! + 1}` : 'N/A' }}</span>
                </div>
              </div>
            </div>

            <!-- Warnings / Missing phases / Crop Suggestion (compact) -->
            <div v-if="sliceInfo.missingPhaseIndices.length > 0" class="mini-warning mb-2">
              Missing segmentation: phases {{ sliceInfo.missingPhaseIndices.join(', ') }}
            </div>
            <div v-if="sliceInfo.excludedImageIndices.length > 0" class="mini-info mb-2">
              Excluded images: {{ sliceInfo.excludedImageIndices.join(', ') }}
            </div>

            <div
              v-if="sliceInfo.esEdPrecheck?.hasCropSuggestion && sliceInfo.esEdPrecheck?.suggestedCrop"
              class="crop-alert-box mb-2"
            >
              <div class="d-flex align-center justify-space-between w-100 ga-2">
                <span class="crop-alert-text">
                  Starts with ES. Recommend crop: <strong>{{ sliceInfo.esEdPrecheck.suggestedCrop.startImageIndex + 1 }}-{{ sliceInfo.esEdPrecheck.suggestedCrop.endImageIndex + 1 }}</strong>
                </span>
                <v-btn
                  color="warning"
                  size="x-small"
                  variant="tonal"
                  prepend-icon="mdi-crop"
                  @click="applyRecommendedCrop(sliceInfo)"
                >
                  Crop
                </v-btn>
              </div>
            </div>
            <div
              v-else-if="sliceInfo.esEdPrecheck?.errorMessage && !sliceInfo.esEdPrecheck?.isValid"
              class="mini-warning mb-2"
            >
              {{ sliceInfo.esEdPrecheck.errorMessage }}
            </div>

            <!-- Pure LV Cavity Area Curve Chart -->
            <div
              v-if="sliceInfo.esEdPrecheck?.lvBpAreaRaw && sliceInfo.esEdPrecheck.lvBpAreaRaw.length > 0"
              class="curve-box"
            >
              <div class="curve-box-header">
                <span class="curve-title">LV Cavity Area Curve</span>
                <div class="curve-legend-tags">
                  <span class="legend-tag">
                    <span class="swatch-line swatch-blue" />
                    <span>Area</span>
                  </span>
                  <span class="legend-tag">
                    <span class="swatch-dash swatch-rose" />
                    <span class="marker-ed-text">ED</span>
                  </span>
                  <span class="legend-tag">
                    <span class="swatch-dash swatch-emerald" />
                    <span class="marker-es-text">ES</span>
                  </span>
                </div>
              </div>

              <OcgCurveChart
                :series="getPreflightSliceSeries(sliceInfo)"
                :n-phases="sliceInfo.esEdPrecheck.lvBpAreaRaw.length"
                :start-phase="sliceInfo.breathHoldStart"
                :es-indices="sliceInfo.esEdPrecheck.esIndices"
                :ed-indices="sliceInfo.esEdPrecheck.edIndices"
                :excluded-phase-indices="sliceInfo.esEdPrecheck.excludedPhaseIndices || []"
                :show-ed-es-markers="true"
                :width="440"
                :height="105"
                :margin="{ top: 12, right: 8, bottom: 18, left: 40 }"
                :format-y="val => Math.round(val).toLocaleString()"
                :clickable="false"
              />
            </div>
          </div>
        </div>

        <!-- Global Analysis Options (Inline single line) -->
        <v-divider class="my-3" />
        <div class="options-row">
          <span class="options-heading">Options:</span>
          <div class="options-items">
            <v-checkbox
              v-model="calculateBMORE"
              label="BMORE"
              density="compact"
              hide-details
            />
            <v-checkbox
              v-model="calculateBMOREMax"
              label="BMORE Max"
              density="compact"
              hide-details
            />
            <v-checkbox
              v-model="calculateVasoconstrictionMap"
              label="Vasoconstriction Map"
              density="compact"
              hide-details
            />
            <v-checkbox
              v-model="calculateV5Markers"
              label="V5 Markers"
              density="compact"
              hide-details
            />
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-5 py-2">
        <v-spacer />
        <v-btn variant="text" size="small" @click="cancelPreflightDialog">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          size="small"
          variant="outlined"
          :disabled="
            isPreflightLoading
              || !!preflightLoadError
              || hasMissingSegmentationPhases
              || hasUnclassifiedOrientations

          "
          @click="startAnalysisFromDialog"
        >
          Start Analysis
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.preflight-dialog {
  background: var(--md-surface);
  border: 1px solid var(--md-outline-variant);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.preflight-body {
  overflow-y: auto;
  max-height: calc(85vh - 110px);
}

.preflight-loading-banner {
  background: var(--md-surface-container);
  border: 1px solid var(--md-outline-variant);
  border-radius: 6px;
  padding: 6px 10px;
}

.slice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 8px;
}

.slice-panel {
  background: var(--md-surface-container-low);
  border: 1px solid var(--md-outline-variant);
  border-radius: 8px;
  padding: 10px 12px;
}

.slice-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slice-title {
  font-weight: 600;
  font-size: 13px;
  color: var(--md-on-surface);
}

.slice-viewer-sub {
  font-size: 11px;
  color: var(--md-on-surface-variant);
  padding: 1px 5px;
  border-radius: 3px;
}

.status-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
}

.chip-success {
  background: var(--md-success-container);
  color: var(--md-on-success-container);
  border: 1px solid var(--md-success);
}

.chip-warning {
  background: var(--md-warning-container);
  color: var(--md-on-warning-container);
  border: 1px solid var(--md-warning);
}

.chip-error {
  background: var(--md-error-container);
  color: var(--md-on-error-container);
  border: 1px solid var(--md-error);
}

.chip-loading {
  background: var(--md-primary-container);
  color: var(--md-on-primary-container);
  border: 1px solid var(--md-primary);
}

.slice-controls-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.slice-classification-wrap {
  flex: 0 0 160px;
  min-width: 140px;
}

.orientation-select :deep(.v-field) {
  font-size: 12px;
}

.slice-meta-strip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 8px;
  padding: 6px 10px;
  background: var(--md-surface-container);
  border: 1px solid var(--md-outline-variant);
  border-radius: 6px;
  min-height: 40px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}

.meta-label {
  color: var(--md-on-surface-variant);
}

.meta-val {
  font-weight: 500;
  color: var(--md-on-surface);
}

.mini-warning {
  font-size: 11px;
  color: var(--md-on-warning-container);
  background: var(--md-warning-container);
  border-left: 2px solid var(--md-warning);
  padding: 2px 6px;
  border-radius: 4px;
}

.mini-info {
  font-size: 11px;
  color: var(--md-on-info-container);
  background: var(--md-info-container);
  border-left: 2px solid var(--md-info);
  padding: 2px 6px;
  border-radius: 4px;
}

.crop-alert-box {
  background: var(--md-warning-container);
  border: 1px solid var(--md-warning);
  border-radius: 6px;
  padding: 4px 8px;
}

.crop-alert-text {
  font-size: 11px;
  color: var(--md-on-warning-container);
}

.curve-box {
  background: var(--md-surface-container-low);
  border: 1px solid var(--md-outline-variant);
  border-radius: 6px;
  padding: 6px 8px 4px 8px;
}

.curve-box-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.curve-title {
  font-size: 10px;
  font-weight: 600;
  color: var(--md-on-surface-variant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.curve-legend-tags {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-tag {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: var(--md-on-surface-variant);
}

.marker-ed-text {
  font-weight: 600;
  color: var(--md-error);
}

.marker-es-text {
  font-weight: 600;
  color: var(--md-success);
}

.swatch-line {
  width: 10px;
  height: 2px;
  border-radius: 1px;
  display: inline-block;
}

.swatch-dash {
  width: 8px;
  height: 2px;
  border-radius: 1px;
  display: inline-block;
}

.swatch-blue {
  background-color: var(--md-primary);
}

.swatch-rose {
  background-color: var(--md-error);
}

.swatch-emerald {
  background-color: var(--md-success);
}

.options-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.options-heading {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--md-on-surface-variant);
}

.options-items {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.options-items :deep(.v-checkbox .v-label) {
  font-size: 12px;
  color: var(--md-on-surface);
}
</style>
